import { useCallback, useEffect, useRef, useState } from "react";
import { StorageScope } from "../../../services/api/instances/storageApi/types/service_types";

const widthCache = new Map<string, number>();

interface UseResizableSidebarOptions {
  service: StorageScope;
  defaultWidth?: number;
  minWidth?: number;
  maxWidth?: number;
}

export const useResizableSidebar = ({
  service,
  defaultWidth = 250,
  minWidth = 150,
  maxWidth = 400,
}: UseResizableSidebarOptions) => {
  const initialWidth = widthCache.get(service) ?? defaultWidth;
  const [width, setWidth] = useState(initialWidth);

  const sidebarRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isResizing = useRef(false);
  const currentWidth = useRef(initialWidth);

  const applyWidth = useCallback((newWidth: number) => {
    if (!sidebarRef.current) return;
    sidebarRef.current.style.width = `${newWidth}px`;
    currentWidth.current = newWidth;
  }, []);

  const commitWidth = useCallback(() => {
    const w = currentWidth.current;
    setWidth(w);
    widthCache.set(service, w);
    document.getElementById("resize-iframe-fix")?.remove();
  }, [service]);

  const startResizing = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizing.current = true;
    const prev = document.body.style.userSelect || "";
    document.body.setAttribute("data-prev-user-select", prev);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    const style = document.createElement("style");
    style.textContent = `
    iframe { pointer-events: none !important; }
  `;
    style.id = "resize-iframe-fix";
    document.head.appendChild(style);
  }, []);

  const stopResizing = useCallback(() => {
    if (!isResizing.current) return;
    isResizing.current = false;
    document.body.style.userSelect =
      document.body.getAttribute("data-prev-user-select") || "";
    document.body.style.cursor = "";
    document.body.removeAttribute("data-prev-user-select");
    commitWidth();
  }, [commitWidth]);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing.current || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      let newWidth = e.clientX - containerRect.left;
      newWidth = Math.min(Math.max(newWidth, minWidth), maxWidth);

      applyWidth(newWidth);
    },
    [minWidth, maxWidth, applyWidth],
  );

  useEffect(() => {
    applyWidth(initialWidth);
  }, [applyWidth, initialWidth]);

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", stopResizing);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", stopResizing);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      document.body.removeAttribute("data-prev-user-select");
    };
  }, [handleMouseMove, stopResizing]);

  return {
    width,
    containerRef,
    sidebarRef,
    startResizing,
  };
};
